import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// The four prototype stylesheets, imported in exactly the order insuronix.css @imports them.
import './styles/variables.css'
import './styles/reset.css'
import './styles/typography.css'
import './styles/components.css'
import './styles/ix-icon-compat.css'
import './styles/polish.css'   // refinement layer - must load last

import App from './App'
import PlatformApp from './platform/PlatformApp'
import { AuthProvider } from './lib/auth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
})

/**
 * One of two applications, never both.
 *
 * `/admin` is the platform portal (admin.insuronix.ai). It is deliberately
 * mounted *instead of* the carrier application rather than inside it: the
 * carrier's `AuthProvider` never mounts here, so the portal cannot call
 * `/auth/me`, cannot hold a carrier session, and has nothing to read if
 * somebody arrives with a carrier cookie still in the jar. Its own session
 * lives in `src/platform/api.ts`, under its own cookie.
 *
 * The split is on the path rather than the host, because one bundle is served
 * on both and a host check here would be a second place for the answer to
 * live. The API is what decides whether the portal exists on a given host
 * (`app/core/platform_auth.require_platform_host`) and answers 404 anywhere
 * else; `platform/auth.tsx` reads that answer and leaves for `/login` when it
 * comes back. Which host serves which half is a web-server rule
 * (deploy/k8s/templates/web.yaml), not this file's business.
 */
function Root() {
  if (window.location.pathname.startsWith('/admin')) {
    return <PlatformApp />
  }
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Root />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
